import { CHAIN_IDS } from "./types.js";
import { unsupported } from "./errors.js";
const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";
const ZERO_BYTES32 = "0x0000000000000000000000000000000000000000000000000000000000000000";
// Base EAS predeploy + registered Sherwood coordination schema UIDs. Shared
// across any chain whose attestations are pinned to Base.
const BASE_EAS = {
    chainId: CHAIN_IDS.BASE,
    address: "0x4200000000000000000000000000000000000021",
    schemas: {
        joinRequest: "0x1e7ce17b16233977ba913b156033e98f52029f4bee273a4abefe6c15ce11d5ef",
        agentApproved: "0x1013f7b38f433b2a93fc5ac162482813081c64edd67cea9b5a90698531ddb607",
    },
};
const NO_EAS = {
    chainId: CHAIN_IDS.BASE,
    address: ZERO_ADDRESS,
    schemas: { joinRequest: ZERO_BYTES32, agentApproved: ZERO_BYTES32 },
};
// Source of truth for each chain: contracts/chains/{chainId}.json.
// Mirror manually until cli/contracts/sdk all share a generated table.
const BASE = {
    chainId: CHAIN_IDS.BASE,
    name: "Base",
    slug: "base",
    sherwood: {
        factory: "0xAC74EC56858d7F1f7618c8e77F65Fc26aDf33c82",
        guardianRegistry: "0x49E4163b5e4b23F8f3d469Cf6fa197FB6b06A26E",
        vaultImpl: "0xfce4bcE08E9C047E4736f75C2B8557e2754Ce36A",
        batchExecutorLib: "0xbC79FbD5036C1Cc4A9d10BDf8628BF09a558496E",
        // Not yet deployed on Base — fill in `STRATEGY_FACTORY` from
        // contracts/chains/8453.json when it lands (issue #387).
        strategyFactory: ZERO_ADDRESS,
    },
    identityRegistry: "0x8004A169FB4a3325136EB29fA0ceB6D2e539a432",
    eas: BASE_EAS,
    tokens: {
        usdc: {
            address: "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913",
            symbol: "USDC",
            decimals: 6,
        },
        weth: {
            address: "0x4200000000000000000000000000000000000006",
            symbol: "WETH",
            decimals: 18,
        },
    },
    explorer: "https://basescan.org",
    rpcs: ["https://mainnet.base.org"],
};
const HYPEREVM = {
    chainId: CHAIN_IDS.HYPEREVM,
    name: "HyperEVM",
    slug: "hyperevm",
    sherwood: {
        factory: "0xd05Ae0E8bcf13075C29817c805d6Cc14F214393a",
        guardianRegistry: "0x8b5710EB4e2fA639F364Dcc3F3B30c8f12F460b9",
        vaultImpl: "0x2cbBe36Cf907A2BB410bacB0e4Fd632C7b012846",
        batchExecutorLib: "0x2c454bEF1b09c8a306a7058b8B510bF0DfF7179D",
        strategyFactory: ZERO_ADDRESS,
    },
    // ERC-8004 identity is minted on Base; HyperEVM funds reference the
    // Base-minted token id. Coordination attestations are pinned to Base EAS.
    identityRegistry: ZERO_ADDRESS,
    eas: BASE_EAS,
    tokens: {
        usdc: {
            address: "0xb88339CB7199b77E23DB6E890353E22632Ba630f",
            symbol: "USDC",
            decimals: 6,
        },
    },
    explorer: "https://hyperliquid.cloud.blockscout.com",
    rpcs: ["https://rpc.hyperliquid.xyz/evm"],
};
// Robinhood Chain mainnet (chain 4663). Sherwood protocol is NOT yet deployed
// here — all sherwood addresses stay ZERO (callers get a clear UNSUPPORTED).
// USDG is the canonical stable (6 decimals); there is NO USDC on this chain.
const ROBINHOOD = {
    chainId: CHAIN_IDS.ROBINHOOD,
    name: "Robinhood Chain",
    slug: "robinhood",
    sherwood: {
        factory: ZERO_ADDRESS,
        guardianRegistry: ZERO_ADDRESS,
        vaultImpl: ZERO_ADDRESS,
        batchExecutorLib: ZERO_ADDRESS,
        strategyFactory: ZERO_ADDRESS,
    },
    // No ERC-8004 registry and no EAS path on Robinhood Chain.
    identityRegistry: ZERO_ADDRESS,
    eas: NO_EAS,
    tokens: {
        weth: {
            address: "0x0Bd7D308f8E1639FAb988df18A8011f41EAcAD73",
            symbol: "WETH",
            decimals: 18,
        },
    },
    explorer: "https://robinhoodchain.blockscout.com",
    rpcs: ["https://rpc.mainnet.chain.robinhood.com"],
};
const ROBINHOOD_L2 = {
    chainId: CHAIN_IDS.ROBINHOOD_L2,
    name: "Robinhood L2 Testnet",
    slug: "robinhood-l2",
    // V2 redeploy (chain 46630): fresh core contracts + a full guardian layer
    // (registry + sWOOD) and a deployed StrategyFactory for the keyless
    // clone+init path (#387).
    sherwood: {
        factory: "0xa5351b91E0A6348a78dA4E47bcd7DFC49fa353F3",
        guardianRegistry: "0xA1b4C376c9C9e7257F654Fe1C4c10f51760407D4",
        vaultImpl: "0x382cCfe80c430De671b15A45A7EDED96b5bFB383",
        batchExecutorLib: "0x039825a3750c27C5f85Bb8486860B867A8D201E5",
        strategyFactory: "0x85a598ADA4fb9654BaF77234864fa100C4B5a204",
    },
    // No ERC-8004 registry and no EAS path on Robinhood L2.
    identityRegistry: ZERO_ADDRESS,
    eas: NO_EAS,
    tokens: {},
    explorer: "https://explorer.testnet.chain.robinhood.com",
    rpcs: ["https://rpc.testnet.chain.robinhood.com"],
};
const REGISTRY = {
    [CHAIN_IDS.BASE]: BASE,
    [CHAIN_IDS.HYPEREVM]: HYPEREVM,
    [CHAIN_IDS.ROBINHOOD]: ROBINHOOD,
    [CHAIN_IDS.ROBINHOOD_L2]: ROBINHOOD_L2,
};
export function getDeployment(chainId) {
    const entry = REGISTRY[chainId];
    if (!entry)
        throw unsupported(`No Sherwood deployment for chain ${chainId}`);
    return entry;
}
export function listDeployments() {
    return Object.values(REGISTRY);
}
/** Network slug for join-request message prefixes (e.g. 8453 → "base"). */
export function chainSlug(chainId) {
    return getDeployment(chainId).slug;
}
